import { timestampDate } from '@bufbuild/protobuf/wkt';
import { Client } from '@connectrpc/connect';

import { Clearable, clearableToWire } from './clearable';
import { ChildOrganizationService } from './espb/child_organization_service_pb';
import { requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto, toSyncUpdateProfile } from './sync';
import {
  ChildOrganization,
  ProfileProfileLockDto,
  OrganizationId,
  ProfileDto,
  ProfileId,
  SyncUpdate,
  LicenseType,
} from './types';

export default class ChildOrganizationServiceClient {
  constructor(private readonly espbClient: Client<typeof ChildOrganizationService>) {}

  async list(
    parentOrganizationId: OrganizationId,
    forceListAll: boolean,
  ): Promise<{ childOrganizations: ChildOrganization[]; delegatedAdminCountLimit: number }> {
    const resp = await this.espbClient.list({
      parentOrganizationId,
      forceListAll,
    });

    const childOrganizations: ChildOrganization[] = resp.childOrganizations.map((co) => ({
      id: requireFieldNotEmpty(co.id),
      name: requireFieldNotEmpty(co.name),
      users: co.users.map((u) => ({
        isAdmin: u.isAdmin,
        isNotConnected: u.isNotConnected,
        hasStartCode: u.hasStartCode,
        lastActiveAt: u.lastActiveAt ? timestampDate(u.lastActiveAt) : undefined,
      })),
      delegatedAdmins: co.delegatedAdmins.map(
        ({ profileId, parentOrganizationProfileId, personalVaultId }) => ({
          profileId,
          parentOrganizationProfileId,
          personalVaultId: personalVaultId || undefined,
        }),
      ),
      createdAt: timestampDate(requireFieldNotEmpty(co.createdAt)),
      parentOrganizationMetadata: co.parentOrganizationMetadata || undefined,
      adminProfile: co.adminProfile ? toSyncUpdateProfile(co.adminProfile) : undefined,
      license:
        co.license.case === 'none' ?
          {
            type: LicenseType.NONE,
            trialEndDate: timestampDate(requireFieldNotEmpty(co.license.value.trialEndDate)),
          }
        : co.license.case === 'example' ? { type: LicenseType.EXAMPLE }
        : co.license.case === 'viaMsp' ?
          {
            type: LicenseType.VIA_MSP,
            contractStartDate: timestampDate(
              requireFieldNotEmpty(co.license.value.contractStartDate),
            ),
            contractEndDate: timestampDate(requireFieldNotEmpty(co.license.value.contractEndDate)),
            licenseCount: co.license.value.licenseCount,
          }
        : { type: LicenseType.UNKNOWN },
    }));

    return {
      childOrganizations,
      delegatedAdminCountLimit: resp.delegatedAdminCountLimit,
    };
  }

  async addDelegatedAdmin(
    childOrganizationId: OrganizationId,
    parentOrganizationProfileId: ProfileId,
    {
      profile,
      adminProfileLock,
    }: {
      profile: ProfileDto;
      adminProfileLock?: ProfileProfileLockDto;
    },
  ) {
    const resp = await this.espbClient.addDelegatedAdmin({
      adminProfileLock,
      childOrganizationId,
      profile,
      parentOrganizationProfileId,
    });

    return {
      profileId: requireFieldNotEmpty(resp.profileId),
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
    };
  }

  async removeDelegatedAdmin(organizationId: OrganizationId, profileId: ProfileId) {
    await this.espbClient.removeDelegatedAdmin({
      organizationId,
      profileId,
    });
  }

  async update(
    childOrganizationId: OrganizationId,
    {
      parentOrganizationMetadata,
      license,
    }: {
      parentOrganizationMetadata?: Clearable<string>;
      license?:
        | { type: LicenseType.NONE; extendTrial?: boolean }
        | { type: LicenseType.VIA_MSP; licenseCount: number };
    },
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.update({
      childOrganizationId,
      parentOrganizationMetadata: clearableToWire(parentOrganizationMetadata),
      license:
        license?.type === LicenseType.NONE ?
          {
            type: { case: 'trial', value: { extend: license.extendTrial } },
          }
        : license?.type === LicenseType.VIA_MSP ?
          {
            type: { case: 'viaMsp', value: { licenseCount: license.licenseCount } },
          }
        : undefined,
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async convertToIndependent(organizationId: OrganizationId) {
    await this.espbClient.convertToIndependent({ organizationId });
  }

  async adopt(
    organizationId: OrganizationId,
    parentOrganizationId: OrganizationId,
    adminProfileLock: ProfileProfileLockDto,
  ): Promise<SyncUpdate> {
    const { syncUpdate } = await this.espbClient.adopt({
      organizationId,
      parentOrganizationId,
      adminProfileLock,
    });
    return toSyncDto(requireFieldNotEmpty(syncUpdate));
  }
}
